import React, {FC, useEffect, useState, useMemo, useRef} from 'react'
import {KTSVG} from '../../../../../_metronic/helpers'
import {useAuth} from '../../../auth'
import {
  forceOverduePlanLineJob,
  getPmsPlans,
  getPmsPlanLines,
  createPmsJob,
  createPmsPlan,
  createPmsPlanLine,
  getRunningHourCounters,
} from '../../core/pms/_requests'
import {getEquipmentByVessel, getSubcomponentsByEquipment} from '../../core/_requests'
import {searchDefects} from '../../../QHSE/core/_requests'
import {toast} from 'react-toastify'
import type {
  PmsJobDto,
  PmsJobDueReason,
  PmsJobNature,
  PmsPlanLineDto,
  PmsWorkType,
  PmsScheduleType,
  PmsCriticality,
  RunningHourCounterDto,
} from '../../core/pms/_models'
import type {EquipmentDto, SubcomponentDto} from '../../core/_models'
import type {Vessel} from '../../../Management/core/_models'
import type {QHSEDefectDto} from '../../../QHSE/core/_models'
import SearchableSelect from '../../../../shared/components/SearchableSelect'

type CompanyGroup = {id: number; name: string}
type Subcompany = {id: number; name: string; companyId: number}

interface Props {
  visible: boolean
  onClose: () => void
  onSuccess: (createdJob?: PmsJobDto) => void
  vessels: Vessel[]
  companies: CompanyGroup[]
  subcompanies: Subcompany[]
  showCompanyFilters?: boolean
  /** Vessel-scoped UI (e.g. assigned vessel); prefer this over legacy `isCrew` prop name */
  isVesselOnlyUser?: boolean
  /** @deprecated Pass `isVesselOnlyUser` instead; kept for call-site compatibility */
  isCrew?: boolean
  initialDefectId?: number
  initialVesselId?: number
  initialEquipmentId?: number
  initialSubComponentId?: number
  initialPlanLineId?: number
  lockPlanLine?: boolean
}

const LABEL = 'form-label fw-semibold fs-6 mb-2 text-dark'
const WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(15, 23, 42, 0.45)',
  position: 'fixed',
  inset: 0,
  zIndex: 2000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backdropFilter: 'blur(2px)',
  padding: '24px',
  overflow: 'auto',
  overscrollBehavior: 'contain',
}

const DEFAULT_JOB_DUE_REASON: PmsJobDueReason = 'OTHER'
const JOB_DUE_REASON_OPTIONS: {value: PmsJobDueReason; label: string}[] = [
  {value: 'DEFECT', label: 'Defect'},
  {value: 'BREAKDOWN', label: 'Breakdown'},
  {value: 'SAFETY_CONCERN', label: 'Safety Concern'},
  {value: 'OTHER', label: 'Other'},
]

// Job Types - single letters only (A-Z)
const JOB_TYPE_WORKTYPE_MAP: Record<string, PmsWorkType> = {
  I: 'INSPECT',
  G: 'CHECK',
  O: 'OVERHAUL',
  C: 'CLEAN',
  A: 'ADJUST',
  R: 'RENEW',
  S: 'CHECK',
  T: 'TEST',
  M: 'CHECK',
  D: 'CHECK',
  L: 'CHECK',
}
const JOB_TYPES: {value: string; label: string; workType: PmsWorkType}[] =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map((letter) => ({
    value: letter,
    label: `${letter} - ${JOB_TYPE_WORKTYPE_MAP[letter] ?? 'INSPECT'}`,
    workType: JOB_TYPE_WORKTYPE_MAP[letter] ?? 'INSPECT',
  }))

const getWorkTypeFromJobType = (jobType: string | null | undefined): PmsWorkType => {
  if (!jobType) return 'INSPECT'
  const normalized = jobType.toUpperCase()
  return JOB_TYPE_WORKTYPE_MAP[normalized] ?? 'INSPECT'
}

const SCHEDULE_TYPES: {value: PmsScheduleType; label: string}[] = [
  {value: 'TIME', label: 'Time-based (Calendar)'},
  {value: 'RUNNING_HOURS', label: 'Running Hours'},
  {value: 'HYBRID_WHICHEVER_FIRST', label: 'Hybrid (Whichever First)'},
  {value: 'HYBRID_BOTH_REQUIRED', label: 'Hybrid (Both Required)'},
]

const TIME_PERIODICITY_TYPES: {value: string; label: string}[] = [
  {value: 'M', label: 'Months (M)'},
  {value: 'W', label: 'Weeks (W)'},
  {value: 'D', label: 'Days (D)'},
]
const HOURS_PERIODICITY_TYPES: {value: string; label: string}[] = [{value: 'H', label: 'Hours (H)'}]
const HYBRID_PERIODICITY_TYPES: {value: string; label: string}[] = [
  ...TIME_PERIODICITY_TYPES,
  ...HOURS_PERIODICITY_TYPES,
]

const CRITICALITY_LEVELS: {value: PmsCriticality; label: string}[] = [
  {value: 'LOW', label: 'Low'},
  {value: 'MEDIUM', label: 'Medium'},
  {value: 'HIGH', label: 'High'},
  {value: 'CRITICAL', label: 'Critical'},
]

const getPeriodicityOptions = (scheduleType?: PmsScheduleType) => {
  if (scheduleType === 'RUNNING_HOURS') return HOURS_PERIODICITY_TYPES
  if (scheduleType === 'TIME') return TIME_PERIODICITY_TYPES
  return HYBRID_PERIODICITY_TYPES
}

export const CreateUnplannedJobModal: FC<Props> = ({
  visible,
  onClose,
  onSuccess,
  vessels,
  companies,
  subcompanies,
  showCompanyFilters = true,
  isVesselOnlyUser: isVesselOnlyUserProp,
  isCrew: isCrewLegacy,
  initialDefectId,
  initialVesselId,
  initialEquipmentId,
  initialSubComponentId,
  initialPlanLineId,
  lockPlanLine = false,
}) => {
  const {currentUser} = useAuth()
  const isVesselOnlyUser = Boolean(isVesselOnlyUserProp ?? isCrewLegacy)
  const [companyId, setCompanyId] = useState<string>('')
  const [subcompanyId, setSubcompanyId] = useState<string>('')
  const [jobNatureType, setJobNatureType] = useState<'SINGLE_TIME' | 'UNPLANNED' | 'RECURRING'>('SINGLE_TIME')
  const [recurringMode, setRecurringMode] = useState<'NEW' | 'EXISTING'>('NEW')

  // Recurring inline fields
  const [recurringScheduleType, setRecurringScheduleType] = useState<PmsScheduleType>('TIME')
  const [recurringJobType, setRecurringJobType] = useState<string>('I')
  const [recurringPeriodicity, setRecurringPeriodicity] = useState<number | undefined>(undefined)
  const [recurringPeriodicityId, setRecurringPeriodicityId] = useState<string | undefined>('M')
  const [recurringPeriodicity2, setRecurringPeriodicity2] = useState<number | undefined>(undefined)
  const [recurringPeriodicityId2, setRecurringPeriodicityId2] = useState<string | undefined>(undefined)
  const [recurringCriticality, setRecurringCriticality] = useState<PmsCriticality>('MEDIUM')
  const [recurringCounterId, setRecurringCounterId] = useState<number | undefined>(undefined)
  const [runningHourCounters, setRunningHourCounters] = useState<RunningHourCounterDto[]>([])
  const [loadingCounters, setLoadingCounters] = useState(false)

  const [formData, setFormData] = useState<{
    vesselId?: number
    equipmentId?: number
    subComponentId?: number
    defectId?: number
    jobDueReason?: PmsJobDueReason
    remarks?: string
    planLineId?: number
    title?: string
    dueDate?: string
    responsibleRank?: string
    workType?: string
  }>({
    vesselId: initialVesselId || undefined,
    equipmentId: initialEquipmentId || undefined,
    subComponentId: initialSubComponentId || undefined,
    defectId: initialDefectId || undefined,
    remarks: '',
    jobDueReason: initialDefectId ? 'DEFECT' : DEFAULT_JOB_DUE_REASON,
    planLineId: initialPlanLineId || undefined,
    title: '',
    dueDate: new Date().toISOString().slice(0, 10),
    responsibleRank: '',
    workType: 'INSPECT',
  })
  const [errors, setErrors] = useState<{[key: string]: string}>({})
  const [submitting, setSubmitting] = useState(false)
  const [equipment, setEquipment] = useState<EquipmentDto[]>([])
  const [subcomponents, setSubcomponents] = useState<SubcomponentDto[]>([])
  const [loadingEquipment, setLoadingEquipment] = useState(false)
  const [planLines, setPlanLines] = useState<PmsPlanLineDto[]>([])
  const [loadingPlanLines, setLoadingPlanLines] = useState(false)
  const [defects, setDefects] = useState<QHSEDefectDto[]>([])
  const [loadingDefects, setLoadingDefects] = useState(false)
  const [activePlanId, setActivePlanId] = useState<number | undefined>(undefined)

  // Track if we're in initial load from defect - skip reset logic during initial load
  const isInitialLoadRef = useRef(false)

  const vesselsForModal = useMemo(() => {
    let list = vessels
    if (companyId) {
      const cid = Number(companyId)
      list = list.filter((v: any) => Number(v.companyGroupAdmin?.id ?? v.companyGroupId) === cid)
    }
    if (subcompanyId) {
      const scid = Number(subcompanyId)
      list = list.filter((v: any) => Number(v.companyAdmin?.id ?? v.companyId) === scid)
    }
    return list
  }, [vessels, companyId, subcompanyId])

  const subcompaniesForCompany = useMemo(() => {
    if (!companyId) return []
    const cid = Number(companyId)
    return subcompanies.filter((sc) => sc.companyId === cid)
  }, [subcompanies, companyId])

  useEffect(() => {
    if (visible) {
      if (initialPlanLineId || lockPlanLine) {
        setJobNatureType('RECURRING')
        setRecurringMode('EXISTING')
      } else {
        setJobNatureType('SINGLE_TIME')
        setRecurringMode('NEW')
      }

      // Reset recurring specific fields
      setRecurringScheduleType('TIME')
      setRecurringJobType('I')
      setRecurringPeriodicity(undefined)
      setRecurringPeriodicityId('M')
      setRecurringPeriodicity2(undefined)
      setRecurringPeriodicityId2(undefined)
      setRecurringCriticality('MEDIUM')
      setRecurringCounterId(undefined)

      // Pre-fill form when opened from defect
      if (initialDefectId || initialVesselId) {
        isInitialLoadRef.current = true

        setFormData((prev) => ({
          ...prev,
          vesselId: initialVesselId || prev.vesselId || undefined,
          equipmentId: initialEquipmentId || prev.equipmentId || undefined,
          subComponentId: initialSubComponentId || prev.subComponentId || undefined,
          defectId: initialDefectId || prev.defectId || undefined,
        }))
        if (initialVesselId) {
          loadEquipment(initialVesselId)
        }
        if (initialEquipmentId) {
          loadSubcomponents(initialEquipmentId)
        }

        setTimeout(() => {
          isInitialLoadRef.current = false
        }, 100)
      }
    } else {
      isInitialLoadRef.current = false
      setJobNatureType('SINGLE_TIME')
      setRecurringMode('NEW')
      setFormData({
        vesselId: initialVesselId || undefined,
        equipmentId: initialEquipmentId || undefined,
        subComponentId: initialSubComponentId || undefined,
        remarks: '',
        jobDueReason: initialDefectId ? 'DEFECT' : DEFAULT_JOB_DUE_REASON,
        defectId: initialDefectId || undefined,
        planLineId: undefined,
        title: '',
        dueDate: new Date().toISOString().slice(0, 10),
        responsibleRank: '',
        workType: 'INSPECT',
      })
      if (!initialVesselId) {
        setCompanyId('')
        setSubcompanyId('')
      }
      setEquipment([])
      setSubcomponents([])
      setPlanLines([])
      setErrors({})
    }
  }, [visible])

  useEffect(() => {
    if (formData.vesselId && formData.vesselId > 0) {
      loadEquipment(formData.vesselId)
      loadPlanLines(formData.vesselId)
      loadDefects()
    } else {
      setEquipment([])
      setSubcomponents([])
      setPlanLines([])
      setDefects([])
    }
  }, [formData.vesselId])

  const loadDefects = async () => {
    if (!formData.vesselId) return
    setLoadingDefects(true)
    try {
      const defectList = await searchDefects({
        vesselId: formData.vesselId,
        activeOnly: true,
      })
      setDefects(defectList)
    } catch (error) {
      console.error('Error loading defects:', error)
    } finally {
      setLoadingDefects(false)
    }
  }

  useEffect(() => {
    if (formData.equipmentId && formData.equipmentId > 0) {
      loadSubcomponents(formData.equipmentId)
    } else {
      setSubcomponents([])
    }
    if (!isInitialLoadRef.current && !initialSubComponentId) {
      setFormData((prev) => ({...prev, subComponentId: undefined, planLineId: undefined}))
    }
  }, [formData.equipmentId])

  // Load running hour counters when needed
  useEffect(() => {
    const needsCounter =
      recurringScheduleType === 'RUNNING_HOURS' ||
      recurringScheduleType === 'HYBRID_WHICHEVER_FIRST' ||
      recurringScheduleType === 'HYBRID_BOTH_REQUIRED'

    if (needsCounter && formData.vesselId && formData.equipmentId) {
      loadRunningHourCounters(formData.vesselId, formData.equipmentId)
    } else {
      setRunningHourCounters([])
    }
  }, [recurringScheduleType, formData.vesselId, formData.equipmentId])

  const loadRunningHourCounters = async (vesselId: number, equipmentId: number) => {
    setLoadingCounters(true)
    try {
      const counters = await getRunningHourCounters(vesselId)
      const filtered = counters.filter((c) => c.equipmentId === equipmentId)
      setRunningHourCounters(filtered)
    } catch (error) {
      console.error('Error loading running hour counters:', error)
    } finally {
      setLoadingCounters(false)
    }
  }

  const loadEquipment = async (vesselId: number) => {
    setLoadingEquipment(true)
    try {
      const data = await getEquipmentByVessel(vesselId)
      setEquipment(data)
    } catch (error) {
      console.error('Error loading equipment:', error)
      toast.error('Failed to load equipment', {position: 'top-center'})
    } finally {
      setLoadingEquipment(false)
    }
  }

  const loadSubcomponents = async (equipmentId: number) => {
    try {
      const data = await getSubcomponentsByEquipment(equipmentId)
      setSubcomponents(data)
    } catch (error) {
      console.error('Error loading subcomponents:', error)
    }
  }

  const loadPlanLines = async (vesselId: number) => {
    setLoadingPlanLines(true)
    try {
      let plans = await getPmsPlans(vesselId)
      let activePlan = plans.find((p) => p.status === 'ACTIVE') ?? plans[0]
      if (!activePlan) {
        try {
          activePlan = await createPmsPlan({
            vesselId,
            name: 'Master Maintenance Plan',
            status: 'ACTIVE',
          })
        } catch (e) {
          console.error('Error auto-creating master plan:', e)
        }
      }
      setActivePlanId(activePlan?.id)
      if (activePlan?.id) {
        const lines = await getPmsPlanLines(activePlan.id)
        setPlanLines(lines)
      } else {
        setPlanLines([])
      }
    } catch (error) {
      console.error('Error loading job plans:', error)
      setPlanLines([])
    } finally {
      setLoadingPlanLines(false)
    }
  }

  const filteredPlanLines = useMemo(() => {
    let list = [...planLines]
    if (formData.equipmentId) {
      list = list.filter((l) => l.equipmentId === formData.equipmentId)
    }
    if (formData.subComponentId) {
      list = list.filter((l) => l.subComponentId === formData.subComponentId)
    }
    return list
  }, [planLines, formData.equipmentId, formData.subComponentId])

  const selectedPlanLine = useMemo(() => {
    if (!formData.planLineId) return undefined
    return planLines.find((line) => line.id === formData.planLineId)
  }, [planLines, formData.planLineId])

  const selectedDefect = useMemo(() => {
    if (!formData.defectId) return undefined
    return defects.find((d) => Number(d.id) === Number(formData.defectId))
  }, [defects, formData.defectId])

  useEffect(() => {
    if (!isInitialLoadRef.current && !lockPlanLine && recurringMode === 'EXISTING') {
      setFormData((prev) => ({...prev, planLineId: undefined}))
    }
  }, [formData.subComponentId, lockPlanLine, recurringMode])

  useEffect(() => {
    if (initialPlanLineId && !formData.planLineId) {
      setFormData((prev) => ({...prev, planLineId: initialPlanLineId}))
    }
  }, [initialPlanLineId, formData.planLineId])

  useEffect(() => {
    if (recurringMode !== 'EXISTING') return
    const defectPlanLineIds =
      selectedDefect?.jobPlanLineIds && selectedDefect.jobPlanLineIds.length > 0
        ? selectedDefect.jobPlanLineIds
        : selectedDefect?.jobPlanLineId != null
        ? [selectedDefect.jobPlanLineId]
        : []
    if (defectPlanLineIds.length !== 1) return
    if (!formData.planLineId) {
      setFormData((prev) => ({
        ...prev,
        planLineId: defectPlanLineIds[0] ?? undefined,
      }))
    }
  }, [selectedDefect?.jobPlanLineIds, selectedDefect?.jobPlanLineId, formData.planLineId, recurringMode])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const newErrors: {[key: string]: string} = {}

    if (!formData.vesselId) {
      newErrors.vesselId = 'Vessel is required'
    }
    if (!formData.equipmentId) {
      newErrors.equipmentId = 'Equipment is required'
    }

    if (jobNatureType === 'SINGLE_TIME' || jobNatureType === 'UNPLANNED') {
      if (!formData.title || !formData.title.trim()) {
        newErrors.title = 'Job Title is required'
      }

      setErrors(newErrors)
      if (Object.keys(newErrors).length > 0) return

      setSubmitting(true)
      try {
        const isUnplanned = jobNatureType === 'UNPLANNED'
        const createdJob = await createPmsJob({
          vesselId: formData.vesselId!,
          equipmentId: formData.equipmentId!,
          subComponentId: formData.subComponentId || undefined,
          defectId: formData.defectId || undefined,
          title: formData.title!.trim(),
          dueDate: formData.dueDate || new Date().toISOString().slice(0, 10),
          responsibleRank: formData.responsibleRank || undefined,
          jobNature: isUnplanned ? 'UNPLANNED' : 'SINGLE_TIME',
          jobDueReason: formData.jobDueReason || (isUnplanned ? 'BREAKDOWN' : DEFAULT_JOB_DUE_REASON),
          remarks: formData.remarks || undefined,
          isBreakdown: isUnplanned,
          state: isUnplanned
            ? 'OVERDUE'
            : (formData.dueDate && formData.dueDate >= new Date().toISOString().slice(0, 10))
            ? 'SCHEDULED'
            : 'OVERDUE',
        })
        toast.success(
          createdJob?.jobCode
            ? `${isUnplanned ? 'Unplanned' : 'Single-time'} job created (${createdJob.jobCode})`
            : `${isUnplanned ? 'Unplanned' : 'Single-time'} job created successfully`,
          {position: 'top-center'}
        )
        onSuccess(createdJob)
        onClose()
      } catch (error: any) {
        console.error('Error creating job:', error)
        toast.error(`Failed to create ${jobNatureType === 'UNPLANNED' ? 'unplanned' : 'single-time'} job`, {position: 'top-center'})
      } finally {
        setSubmitting(false)
      }
    } else if (jobNatureType === 'RECURRING' && recurringMode === 'NEW') {
      // Validate inline recurring fields
      if (!formData.title || !formData.title.trim()) {
        newErrors.title = 'Job Title / Description is required'
      }
      if (!recurringJobType) {
        newErrors.jobType = 'Job type is required'
      }
      if (!recurringScheduleType) {
        newErrors.scheduleType = 'Schedule type is required'
      }

      const isHybrid =
        recurringScheduleType === 'HYBRID_WHICHEVER_FIRST' ||
        recurringScheduleType === 'HYBRID_BOTH_REQUIRED'
      const isTimeOnly = recurringScheduleType === 'TIME'
      const isHoursOnly = recurringScheduleType === 'RUNNING_HOURS'
      const timeTypes = ['M', 'W', 'D']
      const hybridTypes = ['M', 'W', 'D', 'H']

      if (isTimeOnly || isHoursOnly || isHybrid) {
        if (!recurringPeriodicity || recurringPeriodicity <= 0) {
          newErrors.jobPeriodicity = 'Periodicity value is required'
        }
      }

      const periodicityId = recurringPeriodicityId?.toUpperCase()
      if (isTimeOnly) {
        if (!periodicityId || !timeTypes.includes(periodicityId)) {
          newErrors.periodicityId = 'Periodicity type must be M (Months), W (Weeks), or D (Days)'
        }
      }

      if (isHoursOnly) {
        if (periodicityId !== 'H') {
          newErrors.periodicityId = 'Periodicity type must be H (Hours) for running hours schedule'
        }
      }

      if (isHybrid) {
        if (!periodicityId || !hybridTypes.includes(periodicityId)) {
          newErrors.periodicityId =
            'Periodicity type must be M (Months), W (Weeks), D (Days), or H (Hours)'
        }
      }

      const hasPeriodicity2Value = recurringPeriodicity2 != null && recurringPeriodicity2 !== 0
      const hasPeriodicity2Type = !!recurringPeriodicityId2?.trim()

      if (hasPeriodicity2Value || hasPeriodicity2Type) {
        if (!hasPeriodicity2Value) {
          newErrors.jobPeriodicity2 = 'Second periodicity value is required when type is selected'
        } else if ((recurringPeriodicity2 ?? 0) <= 0) {
          newErrors.jobPeriodicity2 = 'Second periodicity value must be greater than 0'
        }

        if (!hasPeriodicity2Type) {
          newErrors.periodicityId2 = 'Second periodicity type is required when value is selected'
        } else {
          const pId2 = (recurringPeriodicityId2 ?? '').toUpperCase()
          if (isTimeOnly && !timeTypes.includes(pId2)) {
            newErrors.periodicityId2 =
              'Second periodicity type must be M (Months), W (Weeks), or D (Days)'
          } else if (isHoursOnly && pId2 !== 'H') {
            newErrors.periodicityId2 =
              'Second periodicity type must be H (Hours) for running hours schedule'
          } else if (isHybrid && !hybridTypes.includes(pId2)) {
            newErrors.periodicityId2 =
              'Second periodicity type must be M (Months), W (Weeks), D (Days), or H (Hours)'
          }
        }
      }

      if ((isHoursOnly || isHybrid) && !recurringCounterId) {
        newErrors.runningHourCounterId = 'Counter is required for running-hours schedules'
      }

      setErrors(newErrors)
      if (Object.keys(newErrors).length > 0) return

      setSubmitting(true)
      try {
        let targetPlanId = activePlanId
        if (!targetPlanId && formData.vesselId) {
          let plans = await getPmsPlans(formData.vesselId)
          let activePlan = plans.find((p) => p.status === 'ACTIVE') ?? plans[0]
          if (!activePlan) {
            activePlan = await createPmsPlan({
              vesselId: formData.vesselId,
              name: 'Master Maintenance Plan',
              status: 'ACTIVE',
            })
          }
          targetPlanId = activePlan?.id
        }

        if (!targetPlanId) {
          throw new Error('No active PMS Plan found for this vessel.')
        }

        const planLineDto: PmsPlanLineDto = {
          planId: targetPlanId,
          taskDescription: formData.title!.trim(),
          jobInstructions: formData.remarks?.trim() ? formData.remarks.trim() : null,
          jobType: recurringJobType || 'I',
          workType: getWorkTypeFromJobType(recurringJobType),
          scheduleType: recurringScheduleType,
          jobPeriodicity: recurringPeriodicity ?? null,
          periodicityId: recurringPeriodicityId ?? null,
          jobPeriodicity2: recurringPeriodicity2 ?? null,
          periodicityId2: recurringPeriodicityId2 ?? null,
          criticality: recurringCriticality || 'MEDIUM',
          active: true,
          equipmentId: formData.equipmentId ?? null,
          componentId: null,
          subComponentId: formData.subComponentId ?? null,
          crewUndertaking: formData.responsibleRank?.trim() || null,
          nextDueDate: formData.dueDate || null,
          runningHourCounterId:
            recurringScheduleType === 'RUNNING_HOURS' ||
            recurringScheduleType === 'HYBRID_WHICHEVER_FIRST' ||
            recurringScheduleType === 'HYBRID_BOTH_REQUIRED'
              ? recurringCounterId ?? null
              : null,
        }

        const createdLine = await createPmsPlanLine(targetPlanId, planLineDto)

        let createdJob: PmsJobDto | undefined
        if (createdLine?.id) {
          try {
            createdJob = await forceOverduePlanLineJob(createdLine.id, {
              jobDueReason: formData.jobDueReason || DEFAULT_JOB_DUE_REASON,
              defectId: formData.defectId,
              remarks: formData.remarks || undefined,
              crewUndertaking: formData.responsibleRank || undefined,
            })
          } catch (jobErr) {
            console.warn('Plan line created, but job instance creation note:', jobErr)
          }
        }

        toast.success(
          createdJob?.jobCode
            ? `Recurring job plan & job created (${createdJob.jobCode})`
            : 'Recurring job plan created successfully',
          {position: 'top-center'}
        )
        onSuccess(createdJob)
        onClose()
      } catch (error: any) {
        console.error('Error creating recurring job plan:', error)
        toast.error('Failed to create recurring job plan', {position: 'top-center'})
      } finally {
        setSubmitting(false)
      }
    } else {
      // Recurring Mode: EXISTING
      if (!formData.planLineId) {
        newErrors.planLineId = 'Job plan is required'
      }

      setErrors(newErrors)
      if (Object.keys(newErrors).length > 0) return

      const defectPlanLineIds =
        selectedDefect?.jobPlanLineIds && selectedDefect.jobPlanLineIds.length > 0
          ? selectedDefect.jobPlanLineIds
          : selectedDefect?.jobPlanLineId != null
          ? [selectedDefect.jobPlanLineId]
          : []
      if (
        defectPlanLineIds.length === 1 &&
        formData.planLineId != null &&
        Number(defectPlanLineIds[0]) !== Number(formData.planLineId)
      ) {
        toast.error('Selected defect is already linked to a different job plan line.', {
          position: 'top-center',
        })
        return
      }

      setSubmitting(true)
      try {
        const createdJob = await forceOverduePlanLineJob(formData.planLineId!, {
          jobDueReason: formData.jobDueReason || DEFAULT_JOB_DUE_REASON,
          defectId: formData.defectId,
          remarks: formData.remarks || undefined,
        })
        toast.success(
          createdJob?.jobCode
            ? `Recurring job instance created (${createdJob.jobCode})`
            : 'Job created successfully',
          {position: 'top-center'}
        )
        onSuccess(createdJob)
        onClose()
      } catch (error: any) {
        console.error('Error creating job:', error)
        toast.error('Failed to create job', {position: 'top-center'})
      } finally {
        setSubmitting(false)
      }
    }
  }

  if (!visible) return null

  return (
    <div
      style={WRAP_STYLE}
      tabIndex={-1}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        role='dialog'
        aria-modal='true'
        style={{
          width: 'min(1400px, 96vw)',
          height: 'min(900px, 92vh)',
          background: '#ffffff',
          borderRadius: '18px',
          boxShadow: '0 20px 60px rgba(15, 23, 42, 0.22)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid #E5E7EB',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '16px',
            flexShrink: 0,
          }}
        >
          <div>
            <h5
              className='mb-1'
              style={{
                margin: 0,
                color: '#111827',
                fontSize: '1.2rem',
                fontWeight: 700,
              }}
            >
              Create Job
            </h5>
            <div style={{color: '#6B7280', fontSize: '0.92rem'}}>
              Create a single-time task, unplanned breakdown job, or recurring periodicity job.
            </div>
          </div>
          <button
            type='button'
            onClick={onClose}
            className='btn btn-sm'
            style={{
              border: '1px solid #D1D5DB',
              background: '#fff',
              color: '#374151',
              width: '38px',
              height: '38px',
              borderRadius: '10px',
              fontSize: '18px',
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
        <form
          onSubmit={handleSubmit}
          className='d-flex flex-column flex-grow-1'
          style={{minHeight: 0}}
        >
          <div
            className='flex-grow-1 overflow-auto px-4 py-3'
            style={{maxHeight: 'calc(92vh - 140px)'}}
          >
            <div className='row g-3'>
              {/* Company filter - disabled when vessel is pre-selected from defect */}
              {showCompanyFilters && !initialVesselId && (
                <div className='col-md-6'>
                  <label className={LABEL}>Company</label>
                  <SearchableSelect
                    className='form-select text-dark'
                    value={companyId}
                    onChange={(e) => {
                      setCompanyId(e.target.value)
                      setSubcompanyId('')
                      setFormData((prev) => ({...prev, vesselId: undefined}))
                    }}
                  >
                    <option value=''>All Companies</option>
                    {companies.map((c) => (
                      <option key={c.id} value={String(c.id)}>
                        {c.name}
                      </option>
                    ))}
                  </SearchableSelect>
                </div>
              )}

              {/* Subcompany filter - disabled when vessel is pre-selected from defect */}
              {showCompanyFilters &&
                !initialVesselId &&
                Boolean(companyId) &&
                subcompaniesForCompany.length > 0 && (
                  <div className='col-md-6'>
                    <label className={LABEL}>Subcompany</label>
                    <SearchableSelect
                      className='form-select text-dark'
                      value={subcompanyId}
                      onChange={(e) => {
                        setSubcompanyId(e.target.value)
                        setFormData((prev) => ({...prev, vesselId: undefined}))
                      }}
                    >
                      <option value=''>All Subcompanies</option>
                      {subcompaniesForCompany.map((sc) => (
                        <option key={sc.id} value={String(sc.id)}>
                          {sc.name}
                        </option>
                      ))}
                    </SearchableSelect>
                  </div>
                )}

              {/* Vessel */}
              <div className='col-md-12'>
                <label className={LABEL}>
                  Vessel <span className='text-danger'>*</span>
                </label>
                {isVesselOnlyUser ? (
                  <input
                    type='text'
                    className='form-control text-dark'
                    value={currentUser?.vessel?.fleet_name || 'Vessel'}
                    disabled
                  />
                ) : (
                  <SearchableSelect
                    className={`form-select text-dark ${errors.vesselId ? 'is-invalid' : ''}`}
                    value={formData.vesselId || ''}
                    onChange={(e) => {
                      const vId = Number(e.target.value)
                      setFormData({...formData, vesselId: vId})
                      if (errors.vesselId) setErrors({...errors, vesselId: ''})
                    }}
                    disabled={!!initialVesselId || lockPlanLine}
                  >
                    <option value=''>Select Vessel</option>
                    {vesselsForModal.map((v) => (
                      <option key={v.id} value={v.id}>
                        {(v as any).fleet_name || `Vessel ${v.id}`}
                      </option>
                    ))}
                  </SearchableSelect>
                )}
                {errors.vesselId && <div className='invalid-feedback'>{errors.vesselId}</div>}
              </div>

              {/* Equipment */}
              {formData.vesselId && formData.vesselId > 0 && (
                <div className='col-md-12'>
                  <label className={LABEL}>
                    Equipment <span className='text-danger'>*</span>{' '}
                    {initialEquipmentId ? '(From Defect)' : ''}
                  </label>
                  {loadingEquipment ? (
                    <div className='text-muted'>Loading equipment...</div>
                  ) : (
                    <SearchableSelect
                      className={`form-select text-dark ${errors.equipmentId ? 'is-invalid' : ''}`}
                      value={formData.equipmentId || ''}
                      onChange={(e) => {
                        const eqId = e.target.value ? Number(e.target.value) : undefined
                        if (eqId === formData.equipmentId) return
                        setFormData((prev) => ({...prev, equipmentId: eqId, subComponentId: undefined, planLineId: undefined}))
                        if (errors.equipmentId) setErrors((prev) => ({...prev, equipmentId: ''}))
                        if (errors.subComponentId) setErrors((prev) => ({...prev, subComponentId: ''}))
                      }}
                      disabled={!!initialEquipmentId || lockPlanLine}
                    >
                      <option value=''>Select Equipment</option>
                      {equipment.map((eq) => (
                        <option key={eq.id} value={eq.id}>
                          {eq.name} {eq.code && `(${eq.code})`}
                        </option>
                      ))}
                    </SearchableSelect>
                  )}
                  {errors.equipmentId && (
                    <div className='invalid-feedback'>{errors.equipmentId}</div>
                  )}
                </div>
              )}

              {/* SubComponent */}
              {formData.equipmentId && formData.equipmentId > 0 && (
                <div className='col-md-6'>
                  <label className={LABEL}>
                    Sub-Component (Optional){' '}
                    {initialSubComponentId ? '(From Defect)' : ''}
                  </label>
                  <SearchableSelect
                    className={`form-select text-dark ${errors.subComponentId ? 'is-invalid' : ''}`}
                    value={formData.subComponentId || ''}
                    onChange={(e) => {
                      const subId = e.target.value ? Number(e.target.value) : undefined
                      setFormData({...formData, subComponentId: subId})
                      if (errors.subComponentId) setErrors({...errors, subComponentId: ''})
                    }}
                    disabled={!!initialSubComponentId || lockPlanLine}
                  >
                    <option value=''>Select Sub-Component (Optional)</option>
                    {subcomponents.map((sub) => (
                      <option key={sub.id} value={sub.id}>
                        {sub.name}
                      </option>
                    ))}
                  </SearchableSelect>
                  {errors.subComponentId && (
                    <div className='invalid-feedback'>{errors.subComponentId}</div>
                  )}
                </div>
              )}

              {/* Job Nature / Type Selector */}
              <div className='col-md-12 mb-2'>
                <label className={LABEL}>Job Classification / Type <span className='text-danger'>*</span></label>
                <div className='d-flex flex-wrap gap-4 p-3 bg-light rounded border'>
                  <div className='form-check'>
                    <input
                      className='form-check-input'
                      type='radio'
                      name='jobNatureType'
                      id='natureSingleTime'
                      checked={jobNatureType === 'SINGLE_TIME'}
                      onChange={() => setJobNatureType('SINGLE_TIME')}
                      disabled={lockPlanLine || !!initialPlanLineId}
                    />
                    <label className='form-check-label fw-bold text-dark' htmlFor='natureSingleTime'>
                      Single-Time Job (One-off Task)
                    </label>
                    <div className='text-muted fs-7'>
                      Standalone one-off task executed once without generating recurring cycles.
                    </div>
                  </div>
                  {/* Unplanned / Defect Job option commented out as there is already an anchor for unplanned jobs
                  <div className='form-check'>
                    <input
                      className='form-check-input'
                      type='radio'
                      name='jobNatureType'
                      id='natureUnplanned'
                      checked={jobNatureType === 'UNPLANNED'}
                      onChange={() => setJobNatureType('UNPLANNED')}
                      disabled={lockPlanLine || !!initialPlanLineId}
                    />
                    <label className='form-check-label fw-bold text-dark' htmlFor='natureUnplanned'>
                      Unplanned / Defect Job
                    </label>
                    <div className='text-muted fs-7'>
                      Emergency repair or breakdown task logged from a defect or failure.
                    </div>
                  </div>
                  */}
                  <div className='form-check'>
                    <input
                      className='form-check-input'
                      type='radio'
                      name='jobNatureType'
                      id='natureRecurring'
                      checked={jobNatureType === 'RECURRING'}
                      onChange={() => setJobNatureType('RECURRING')}
                    />
                    <label className='form-check-label fw-bold text-dark' htmlFor='natureRecurring'>
                      Recurring Periodicity Job
                    </label>
                    <div className='text-muted fs-7'>
                      Linked to a periodicity plan line (Calendar / Running Hours cycle).
                    </div>
                  </div>
                </div>
              </div>

              {jobNatureType === 'SINGLE_TIME' || jobNatureType === 'UNPLANNED' ? (
                <>
                  <div className='col-md-8'>
                    <label className={LABEL}>
                      Job Title / Task Description <span className='text-danger'>*</span>
                    </label>
                    <input
                      type='text'
                      className={`form-control text-dark ${errors.title ? 'is-invalid' : ''}`}
                      placeholder='e.g., One-time inspection of main engine fuel valves'
                      value={formData.title || ''}
                      onChange={(e) => {
                        setFormData({...formData, title: e.target.value})
                        if (errors.title) setErrors({...errors, title: ''})
                      }}
                    />
                    {errors.title && <div className='invalid-feedback'>{errors.title}</div>}
                  </div>
                  <div className='col-md-4'>
                    <label className={LABEL}>Target Due Date</label>
                    <input
                      type='date'
                      className='form-control text-dark'
                      value={formData.dueDate || ''}
                      onChange={(e) => setFormData({...formData, dueDate: e.target.value})}
                    />
                  </div>
                  <div className='col-md-6'>
                    <label className={LABEL}>Responsible Rank / Assignee</label>
                    <input
                      type='text'
                      className='form-control text-dark'
                      placeholder='e.g., C/E, 2/E, E/O, Electrician'
                      value={formData.responsibleRank || ''}
                      onChange={(e) => setFormData({...formData, responsibleRank: e.target.value})}
                    />
                  </div>
                </>
              ) : (
                /* Recurring Periodicity Job Section */
                <div className='col-md-12'>
                  <div className='card border border-primary-subtle bg-light-subtle mb-3'>
                    <div className='card-header py-3 px-4 d-flex align-items-center justify-content-between bg-light'>
                      <div className='d-flex gap-4'>
                        <div className='form-check'>
                          <input
                            className='form-check-input'
                            type='radio'
                            name='recurringMode'
                            id='recurringModeNew'
                            checked={recurringMode === 'NEW'}
                            onChange={() => setRecurringMode('NEW')}
                            disabled={lockPlanLine || !!initialPlanLineId}
                          />
                          <label className='form-check-label fw-bold text-dark' htmlFor='recurringModeNew'>
                            Define New Recurring Schedule
                          </label>
                        </div>
                        <div className='form-check'>
                          <input
                            className='form-check-input'
                            type='radio'
                            name='recurringMode'
                            id='recurringModeExisting'
                            checked={recurringMode === 'EXISTING'}
                            onChange={() => setRecurringMode('EXISTING')}
                          />
                          <label className='form-check-label fw-bold text-dark' htmlFor='recurringModeExisting'>
                            Select Existing Plan Line
                          </label>
                        </div>
                      </div>
                      <span className={`badge ${recurringMode === 'NEW' ? 'badge-light-primary' : 'badge-light-info'} fs-8`}>
                        {recurringMode === 'NEW' ? 'New Recurring Plan Line' : 'Existing Plan Line'}
                      </span>
                    </div>
                    <div className='card-body p-4'>
                      {recurringMode === 'NEW' ? (
                        <div className='row g-3'>
                          {/* Task Description */}
                          <div className='col-md-12'>
                            <label className={LABEL}>
                              Job Description / Title <span className='text-danger'>*</span>
                            </label>
                            <input
                              type='text'
                              className={`form-control text-dark ${errors.title ? 'is-invalid' : ''}`}
                              placeholder='e.g., Monthly Inspection of Main Engine Fuel Valves'
                              value={formData.title || ''}
                              onChange={(e) => {
                                setFormData({...formData, title: e.target.value})
                                if (errors.title) setErrors({...errors, title: ''})
                              }}
                            />
                            {errors.title && <div className='invalid-feedback'>{errors.title}</div>}
                          </div>

                          {/* Job Type & Schedule Type */}
                          <div className='col-md-6'>
                            <label className={LABEL}>
                              Job Type <span className='text-danger'>*</span>
                            </label>
                            <SearchableSelect
                              className={`form-select text-dark ${errors.jobType ? 'is-invalid' : ''}`}
                              value={recurringJobType}
                              onChange={(e) => {
                                setRecurringJobType(e.target.value)
                                if (errors.jobType) setErrors({...errors, jobType: ''})
                              }}
                            >
                              {JOB_TYPES.map((jt) => (
                                <option key={jt.value} value={jt.value}>
                                  {jt.label}
                                </option>
                              ))}
                            </SearchableSelect>
                            {errors.jobType && <div className='invalid-feedback'>{errors.jobType}</div>}
                          </div>

                          <div className='col-md-6'>
                            <label className={LABEL}>
                              Schedule Type <span className='text-danger'>*</span>
                            </label>
                            <SearchableSelect
                              className={`form-select text-dark ${errors.scheduleType ? 'is-invalid' : ''}`}
                              value={recurringScheduleType}
                              onChange={(e) => {
                                const st = e.target.value as PmsScheduleType
                                const isHybrid = st === 'HYBRID_WHICHEVER_FIRST' || st === 'HYBRID_BOTH_REQUIRED'
                                const needsHours = st === 'RUNNING_HOURS' || isHybrid
                                setRecurringScheduleType(st)
                                if (st === 'RUNNING_HOURS' && recurringPeriodicityId !== 'H') {
                                  setRecurringPeriodicityId('H')
                                } else if (st === 'TIME' && recurringPeriodicityId === 'H') {
                                  setRecurringPeriodicityId('M')
                                }
                                if (!needsHours) {
                                  setRecurringCounterId(undefined)
                                  setErrors((prev) => ({...prev, runningHourCounterId: ''}))
                                }
                              }}
                            >
                              {SCHEDULE_TYPES.map((st) => (
                                <option key={st.value} value={st.value}>
                                  {st.label}
                                </option>
                              ))}
                            </SearchableSelect>
                            {errors.scheduleType && <div className='invalid-feedback'>{errors.scheduleType}</div>}
                          </div>

                          {/* Running Hour Counter selector when applicable */}
                          {(recurringScheduleType === 'RUNNING_HOURS' ||
                            recurringScheduleType === 'HYBRID_WHICHEVER_FIRST' ||
                            recurringScheduleType === 'HYBRID_BOTH_REQUIRED') && (
                            <div className='col-md-12'>
                              <label className={LABEL}>
                                Running Hour Counter <span className='text-danger'>*</span>
                              </label>
                              {loadingCounters ? (
                                <div className='text-muted'>Loading counters...</div>
                              ) : runningHourCounters.length > 0 ? (
                                <>
                                  <SearchableSelect
                                    className={`form-select text-dark ${errors.runningHourCounterId ? 'is-invalid' : ''}`}
                                    value={recurringCounterId ?? ''}
                                    onChange={(e) => {
                                      const v = e.target.value ? Number(e.target.value) : undefined
                                      setRecurringCounterId(v)
                                      setErrors((prev) => ({...prev, runningHourCounterId: ''}))
                                    }}
                                  >
                                    <option value=''>Select Counter</option>
                                    {runningHourCounters.map((c) => (
                                      <option key={c.id} value={c.id}>
                                        {c.name} (Current: {c.currentValue} hrs)
                                      </option>
                                    ))}
                                  </SearchableSelect>
                                  {errors.runningHourCounterId && (
                                    <div className='invalid-feedback'>{errors.runningHourCounterId}</div>
                                  )}
                                </>
                              ) : (
                                <div className='border rounded p-2 bg-light-warning'>
                                  <span className='text-warning'>
                                    No running hour counters configured for this equipment.
                                  </span>
                                  <div className='text-muted fs-7 mt-1'>
                                    Add a counter under Running Hours management if you wish to track machinery runtime.
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Periodicities Box */}
                          <div className='col-md-12'>
                            <label className={LABEL}>
                              Periodicities <span className='text-danger'>*</span>
                            </label>
                            <div className='border rounded p-3 bg-light'>
                              <div className='row g-3'>
                                <div className='col-md-6'>
                                  <label className='form-label fs-7 text-muted mb-1'>
                                    Periodicity 1 <span className='text-danger'>*</span>
                                  </label>
                                  <div className='row g-2'>
                                    <div className='col-7'>
                                      <input
                                        type='number'
                                        className={`form-control form-control-sm text-dark ${
                                          errors.jobPeriodicity ? 'is-invalid' : ''
                                        }`}
                                        placeholder='Value'
                                        min={1}
                                        value={recurringPeriodicity ?? ''}
                                        onChange={(e) => {
                                          setRecurringPeriodicity(e.target.value ? Number(e.target.value) : undefined)
                                          if (errors.jobPeriodicity) setErrors({...errors, jobPeriodicity: ''})
                                        }}
                                      />
                                      {errors.jobPeriodicity && (
                                        <div className='invalid-feedback'>{errors.jobPeriodicity}</div>
                                      )}
                                    </div>
                                    <div className='col-5'>
                                      <SearchableSelect
                                        className={`form-select form-select-sm text-dark ${
                                          errors.periodicityId ? 'is-invalid' : ''
                                        }`}
                                        value={recurringPeriodicityId || ''}
                                        onChange={(e) => {
                                          setRecurringPeriodicityId(e.target.value || undefined)
                                          if (errors.periodicityId) setErrors({...errors, periodicityId: ''})
                                        }}
                                      >
                                        <option value=''>Type</option>
                                        {getPeriodicityOptions(recurringScheduleType).map((pt) => (
                                          <option key={pt.value} value={pt.value}>
                                            {pt.label}
                                          </option>
                                        ))}
                                      </SearchableSelect>
                                      {errors.periodicityId && (
                                        <div className='invalid-feedback'>{errors.periodicityId}</div>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                <div className='col-md-6'>
                                  <label className='form-label fs-7 text-muted mb-1'>
                                    Periodicity 2 (Optional)
                                  </label>
                                  <div className='row g-2'>
                                    <div className='col-7'>
                                      <input
                                        type='number'
                                        className={`form-control form-control-sm text-dark ${
                                          errors.jobPeriodicity2 ? 'is-invalid' : ''
                                        }`}
                                        placeholder='Value'
                                        min={1}
                                        value={recurringPeriodicity2 ?? ''}
                                        onChange={(e) => {
                                          setRecurringPeriodicity2(e.target.value ? Number(e.target.value) : undefined)
                                          if (errors.jobPeriodicity2) setErrors({...errors, jobPeriodicity2: ''})
                                        }}
                                      />
                                      {errors.jobPeriodicity2 && (
                                        <div className='invalid-feedback'>{errors.jobPeriodicity2}</div>
                                      )}
                                    </div>
                                    <div className='col-5'>
                                      <SearchableSelect
                                        className={`form-select form-select-sm text-dark ${
                                          errors.periodicityId2 ? 'is-invalid' : ''
                                        }`}
                                        value={recurringPeriodicityId2 || ''}
                                        onChange={(e) => {
                                          setRecurringPeriodicityId2(e.target.value || undefined)
                                          if (errors.periodicityId2) setErrors({...errors, periodicityId2: ''})
                                        }}
                                      >
                                        <option value=''>Type</option>
                                        {getPeriodicityOptions(recurringScheduleType).map((pt) => (
                                          <option key={pt.value} value={pt.value}>
                                            {pt.label}
                                          </option>
                                        ))}
                                      </SearchableSelect>
                                      {errors.periodicityId2 && (
                                        <div className='invalid-feedback'>{errors.periodicityId2}</div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Criticality & Responsible Rank & Due Date */}
                          <div className='col-md-4'>
                            <label className={LABEL}>Criticality</label>
                            <SearchableSelect
                              className='form-select text-dark'
                              value={recurringCriticality}
                              onChange={(e) => setRecurringCriticality(e.target.value as PmsCriticality)}
                            >
                              {CRITICALITY_LEVELS.map((c) => (
                                <option key={c.value} value={c.value}>
                                  {c.label}
                                </option>
                              ))}
                            </SearchableSelect>
                          </div>

                          <div className='col-md-4'>
                            <label className={LABEL}>Responsible Rank</label>
                            <input
                              type='text'
                              className='form-control text-dark'
                              placeholder='e.g., C/E, 2/E, E/O'
                              value={formData.responsibleRank || ''}
                              onChange={(e) => setFormData({...formData, responsibleRank: e.target.value})}
                            />
                          </div>

                          <div className='col-md-4'>
                            <label className={LABEL}>Target Due Date</label>
                            <input
                              type='date'
                              className='form-control text-dark'
                              value={formData.dueDate || ''}
                              onChange={(e) => setFormData({...formData, dueDate: e.target.value})}
                            />
                          </div>
                        </div>
                      ) : (
                        /* Existing Plan Line Selection */
                        <div className='row g-3'>
                          <div className='col-md-12'>
                            <label className={LABEL}>
                              Select Existing Job Plan <span className='text-danger'>*</span>
                            </label>
                            {loadingPlanLines ? (
                              <div className='text-muted'>Loading job plans...</div>
                            ) : (
                              <>
                                <SearchableSelect
                                  className={`form-select text-dark ${errors.planLineId ? 'is-invalid' : ''}`}
                                  value={formData.planLineId || ''}
                                  onChange={(e) => {
                                    const value = e.target.value ? Number(e.target.value) : undefined
                                    setFormData({...formData, planLineId: value})
                                    if (errors.planLineId) setErrors({...errors, planLineId: ''})
                                  }}
                                  disabled={lockPlanLine}
                                >
                                  <option value=''>Select Job Plan</option>
                                  {filteredPlanLines.map((line) => (
                                    <option key={line.id} value={line.id}>
                                      {line.jobCode ? `${line.jobCode} - ` : ''}
                                      {line.taskDescription}
                                    </option>
                                  ))}
                                </SearchableSelect>
                                {!loadingPlanLines && filteredPlanLines.length === 0 && (
                                  <div className='text-muted fs-7 mt-2'>
                                    No existing recurring job plans found for this selection. Select "Define New Recurring Schedule" above to create one.
                                  </div>
                                )}
                              </>
                            )}
                            {errors.planLineId && <div className='invalid-feedback d-block'>{errors.planLineId}</div>}
                          </div>

                          {selectedPlanLine && (
                            <div className='col-md-12'>
                              <div className='border rounded p-3 bg-white'>
                                <div className='fw-semibold text-dark mb-2'>Selected Job Plan Details</div>
                                <div className='mb-1'>
                                  <strong>Plan Job Code:</strong> {selectedPlanLine.jobCode || '-'}
                                </div>
                                <div className='mb-1'>
                                  <strong>Job Description:</strong> {selectedPlanLine.taskDescription || '-'}
                                </div>
                                <div className='mb-1'>
                                  <strong>Job Instructions:</strong> {selectedPlanLine.jobInstructions || '-'}
                                </div>
                                <div className='mb-1'>
                                  <strong>Assigned To:</strong> {selectedPlanLine.crewUndertaking || '-'}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className='col-md-12'>
                <div className='border rounded p-3 bg-light-primary'>
                  <div className='fw-semibold text-dark mb-1'>Job ID</div>
                  <div className='text-dark'>
                    A unique job code will be auto-generated when this job is created.
                  </div>
                  <div className='text-muted fs-7 mt-1'>
                    Use that Job ID to search and identify this job later in the PMS jobs list.
                  </div>
                </div>
              </div>

              <div className='col-md-6'>
                <label className={LABEL}>Job Due Reason</label>
                <SearchableSelect
                  className='form-select text-dark'
                  value={formData.jobDueReason || DEFAULT_JOB_DUE_REASON}
                  onChange={(e) => {
                    const value = e.target.value as PmsJobDueReason
                    setFormData({...formData, jobDueReason: value})
                  }}
                >
                  {JOB_DUE_REASON_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </SearchableSelect>
                <div className='form-text text-muted'>
                  Pick the best reason for why this job is overdue. Use “Defect” when linked to an
                  issue; otherwise choose an ad-hoc reason.
                </div>
              </div>

              {/* Info about unplanned/recurring jobs */}
              <div className='col-md-12'>
                <div className='alert alert-info d-flex align-items-center mb-0'>
                  <KTSVG
                    path='/media/icons/duotune/general/gen044.svg'
                    className='svg-icon-2 me-2'
                  />
                  <div>
                    {jobNatureType === 'RECURRING' ? (
                      <span>
                        <strong>Recurring Periodicity Jobs</strong> establish or instantiate a recurring maintenance cycle tied to your vessel's master PMS plan.
                      </span>
                    ) : (
                      <span>
                        <strong>Unplanned / Single-time Jobs</strong> let you create a standalone job without affecting regular maintenance periodicities.
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Link to Defect */}
              {formData.vesselId && formData.vesselId > 0 && (
                <div className='col-md-12'>
                  <label className={LABEL}>
                    Link to Defect {initialDefectId ? '(Pre-filled from defect)' : '(Optional)'}
                  </label>
                  {loadingDefects ? (
                    <div className='text-muted'>Loading defects...</div>
                  ) : (
                    <SearchableSelect
                      className='form-select text-dark'
                      value={formData.defectId || ''}
                      onChange={(e) => {
                        const defectId = e.target.value ? Number(e.target.value) : undefined
                        setFormData({...formData, defectId})
                      }}
                      disabled={!!initialDefectId}
                    >
                      <option value=''>No Defect Link</option>
                      {defects.map((defect) => {
                        const jobLabels =
                          defect.jobPlanLineJobCodes
                            ?.map((code, idx) => {
                              const task = defect.jobPlanLineTaskDescriptions?.[idx]
                              if (code && task) return `${code} - ${task}`
                              if (code) return code
                              const id = defect.jobPlanLineIds?.[idx]
                              return id ? `Plan #${id}` : ''
                            })
                            .filter((v) => v && v.trim().length > 0) || []
                        const jobSuffix =
                          jobLabels.length > 0
                            ? ` (Jobs: ${jobLabels.join(', ')})`
                            : defect.jobPlanLineJobCode
                            ? ` (Job ${defect.jobPlanLineJobCode})`
                            : defect.jobPlanLineId
                            ? ` (Job Plan #${defect.jobPlanLineId})`
                            : ''
                        return (
                          <option key={defect.id} value={defect.id}>
                            {defect.defectNumber || `Defect #${defect.id}`} -{' '}
                            {defect.description?.substring(0, 50) || 'No description'}
                            {jobSuffix}
                          </option>
                        )
                      })}
                    </SearchableSelect>
                  )}
                </div>
              )}

              {/* Remarks */}
              <div className='col-md-12'>
                <label className={LABEL}>Remarks / Instructions</label>
                <textarea
                  className='form-control text-dark'
                  rows={3}
                  value={formData.remarks || ''}
                  onChange={(e) => setFormData({...formData, remarks: e.target.value})}
                  placeholder='Additional notes or instructions'
                />
              </div>
            </div>
          </div>
          <div className='border-top d-flex justify-content-end gap-2 px-4 py-3 flex-shrink-0'>
            <button type='button' className='btn btn-light btn-sm' onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type='submit' className='btn btn-primary btn-sm' disabled={submitting}>
              {submitting ? 'Creating...' : 'Create Job'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

